import type { Theme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';
import type { ActionInteraction } from './ActionPressable';

export type ButtonVariant =
  | 'primary'
  | 'secondaryTonal'
  | 'secondaryNeutral'
  | 'tertiary'
  | 'ghost'
  | 'textPrimary'
  | 'textSecondary'
  | 'destructive';
export type ButtonSize = 'small' | 'medium' | 'large';
export const buttonPadding = {
  small: 'spacing.general.x2',
  medium: 'spacing.general.x3',
  large: 'spacing.general.x6',
} as const;
export const buttonPaddingVertical = {
  small: 'spacing.general.x1',
  medium: 'spacing.general.x2',
  large: 'spacing.general.x4',
} as const;
export const buttonIconSize = {
  small: 'sizing.icon.s',
  medium: 'sizing.icon.sm',
  large: 'sizing.icon.default',
} as const;
export const iconButtonSize = {
  xSmall: 'sizing.general.x6',
  small: 'sizing.general.x8',
  medium: 'sizing.general.x10',
  large: 'sizing.general.x12',
} as const;

export function buttonPresentation(
  theme: Theme,
  variant: ButtonVariant,
  inverted: boolean,
  interaction: ActionInteraction,
  iconOnly = false,
) {
  const { disabled, loading, pressed, hovered } = interaction;
  const colors = theme.colors;
  const primaryDisabled = disabled && variant === 'primary' && !inverted;
  const active = pressed || hovered;
  let backgroundColor = 'transparent';
  let foregroundColor = colors['text.brand'];
  let spinnerColor = colors[inverted ? 'border.inverse.primary' : 'border.brand'];
  let borderColor: string | undefined;
  let overlayColor: string | undefined;
  let borderWidth = 0;
  switch (variant) {
    case 'primary':
      backgroundColor =
        colors[
          primaryDisabled
            ? 'component.button.bg.tertiary'
            : inverted
              ? 'component.button.bg.inverted'
              : 'component.button.bg.primary'
        ];
      foregroundColor =
        colors[
          primaryDisabled
            ? iconOnly
              ? 'icon.disabled'
              : 'text.disabled'
            : inverted
              ? iconOnly
                ? 'icon.brand'
                : 'text.brand'
              : iconOnly
                ? 'icon.inverse.primary'
                : 'text.inverse.primary'
        ];
      spinnerColor = colors[inverted ? 'border.brand' : 'border.inverse.strong'];
      overlayColor = active
        ? colors[
            inverted
              ? pressed
                ? 'state-layer.press-brand'
                : 'state-layer.hover-brand'
              : pressed
                ? 'component.button.bg.pressed'
                : 'component.button.bg.hover'
          ]
        : undefined;
      break;
    case 'secondaryTonal':
      backgroundColor = colors['component.button.bg.secondary'];
      foregroundColor = colors.black;
      spinnerColor = colors.black;
      overlayColor = active ? colors[pressed ? 'state-layer.press-alt' : 'state-layer.hover-alt'] : undefined;
      break;
    case 'secondaryNeutral':
      backgroundColor = colors['component.button.bg.tertiary'];
      foregroundColor = colors[iconOnly ? 'icon.primary' : 'text.primary'];
      spinnerColor = colors[iconOnly ? 'icon.primary' : 'icon.secondary'];
      overlayColor = active ? colors[pressed ? 'state-layer.press' : 'state-layer.hover'] : undefined;
      break;
    case 'tertiary':
      foregroundColor =
        colors[
          inverted
            ? iconOnly
              ? 'icon.inverse.primary'
              : 'text.inverse.primary'
            : iconOnly
              ? 'component.button.icon.tertiary'
              : 'component.button.label.tertiary'
        ];
      borderColor =
        colors[
          inverted
            ? 'border.inverse.primary'
            : loading
              ? 'border.secondary'
              : iconOnly && active
                ? 'border.brand'
                : 'component.button.border.tertiary'
        ];
      borderWidth = theme.borderWidths[active ? 'border-width.emphasized' : 'border-width.standard'];
      spinnerColor = colors[inverted ? 'border.inverse.strong' : 'border.brand'];
      overlayColor = active
        ? colors[
            inverted
              ? pressed
                ? 'state-layer.inverse.press'
                : 'state-layer.inverse.hover'
              : iconOnly
                ? pressed
                  ? 'state-layer.press-brand'
                  : 'state-layer.hover-brand'
                : pressed
                  ? 'state-layer.press'
                  : 'state-layer.hover'
          ]
        : undefined;
      break;
    case 'ghost':
    case 'textPrimary':
      foregroundColor =
        colors[
          inverted
            ? iconOnly
              ? 'icon.inverse.primary'
              : 'text.inverse.primary'
            : variant === 'textPrimary'
              ? 'text.interactive'
              : iconOnly
                ? 'icon.brand'
                : 'text.brand'
        ];
      overlayColor = active
        ? colors[
            inverted && variant === 'textPrimary'
              ? pressed
                ? 'state-layer.inverse.press'
                : 'state-layer.inverse.hover'
              : pressed
                ? 'state-layer.press-brand'
                : 'state-layer.hover-brand'
          ]
        : undefined;
      break;
    case 'textSecondary':
      foregroundColor = colors[iconOnly ? 'icon.primary' : 'text.primary'];
      spinnerColor = colors['icon.primary'];
      overlayColor = active ? colors[pressed ? 'state-layer.press' : 'state-layer.hover'] : undefined;
      break;
    case 'destructive':
      backgroundColor = colors[inverted ? 'surface.default' : 'surface.error.medium'];
      foregroundColor = colors[inverted ? 'text.error' : 'white'];
      spinnerColor = colors[inverted ? 'border.error' : 'white'];
      overlayColor = active
        ? colors[
            inverted
              ? pressed
                ? 'state-layer.press'
                : 'state-layer.hover'
              : pressed
                ? 'state-layer.inverse.press-on-color'
                : 'state-layer.inverse.hover-on-color'
          ]
        : undefined;
  }
  return {
    backgroundColor,
    foregroundColor,
    spinnerColor,
    borderColor,
    borderWidth,
    overlayColor,
    opacity: disabled && !primaryDisabled ? INTERACTION.opacity['040'] : 1,
  };
}
